import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import IntervalInput, {
  parseIntervalInput,
  intervalToLatex,
} from "../../../../components/inputs/IntervalInput";
import ParabolaShape from "../../../../components/visuals/ParabolaShape";
import NumberLineRanges from "../../../../components/visuals/NumberLineRanges";

function randInt(min, max, exclude = []) {
  let n;
  do {
    n = Math.floor(Math.random() * (max - min + 1)) + min;
  } while (exclude.includes(n));
  return n;
}

function choice(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function cubicLatex(a, b, c, d) {
  const parts = [];
  const pushTerm = (coef, core) => {
    if (coef === 0) return;
    const abs = Math.abs(coef);
    const body = abs === 1 && core ? core : `${abs}${core}`;
    if (parts.length === 0) parts.push(coef < 0 ? `-${body}` : body);
    else parts.push(coef < 0 ? `- ${body}` : `+ ${body}`);
  };
  pushTerm(a, "x^3");
  pushTerm(b, "x^2");
  pushTerm(c, "x");
  if (d !== 0) parts.push(d < 0 ? `- ${Math.abs(d)}` : `+ ${d}`);
  return parts.join(" ");
}

function derivativeLatex(a, b, c) {
  const parts = [];
  const pushTerm = (coef, core) => {
    if (coef === 0) return;
    const abs = Math.abs(coef);
    const body = abs === 1 && core ? core : `${abs}${core}`;
    if (parts.length === 0) parts.push(coef < 0 ? `-${body}` : body);
    else parts.push(coef < 0 ? `- ${body}` : `+ ${body}`);
  };
  pushTerm(a, "x^2");
  pushTerm(b, "x");
  if (c !== 0) parts.push(c < 0 ? `- ${Math.abs(c)}` : `+ ${c}`);
  return parts.join(" ");
}

function eq(a, b, tol = 1e-9) {
  if (a === b) return true;
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  return Math.abs(a - b) <= tol;
}

function intervalEq(A, B) {
  return eq(A.left, B.left)
    && eq(A.right, B.right)
    && A.leftClosed === B.leftClosed
    && A.rightClosed === B.rightClosed;
}

function sortIntervals(arr) {
  return [...arr].sort((u, v) => {
    if (u.left === v.left) {
      if (u.right === v.right) return 0;
      if (u.right === Infinity) return 1;
      if (v.right === Infinity) return -1;
      return u.right - v.right;
    }
    if (u.left === -Infinity) return -1;
    if (v.left === -Infinity) return 1;
    return u.left - v.left;
  });
}

function parseSingleInterval(raw) {
  const parsed = parseIntervalInput(raw);
  if (!parsed || parsed.kind !== "intervals" || parsed.intervals.length !== 1) return null;
  return parsed.intervals[0];
}

function buildTaskData() {
  const r1 = randInt(-3, 1);
  const r2 = randInt(r1 + 1, 4);
  const k = choice([-12, -6, 6, 12]); // f'(x)=k(x-r1)(x-r2)
  const d = randInt(-5, 5);

  const da = k;
  const db = -k * (r1 + r2);
  const dc = k * r1 * r2;

  // Integral: f(x)=k/3 x^3 - k(r1+r2)/2 x^2 + k r1r2 x + d
  const a = k / 3;
  const b = (-k * (r1 + r2)) / 2;
  const c = k * r1 * r2;

  const askIncreasing = Math.random() < 0.5;
  const askLabel = askIncreasing ? "rosnaca" : "malejaca";

  let expected;
  if (k > 0) {
    expected = askIncreasing
      ? [
          { left: -Infinity, right: r1, leftClosed: false, rightClosed: false },
          { left: r2, right: Infinity, leftClosed: false, rightClosed: false },
        ]
      : [{ left: r1, right: r2, leftClosed: false, rightClosed: false }];
  } else {
    expected = askIncreasing
      ? [{ left: r1, right: r2, leftClosed: false, rightClosed: false }]
      : [
          { left: -Infinity, right: r1, leftClosed: false, rightClosed: false },
          { left: r2, right: Infinity, leftClosed: false, rightClosed: false },
        ];
  }

  const leftSign = k > 0 ? "+" : "-";
  const middleSign = k > 0 ? "-" : "+";
  const rightSign = k > 0 ? "+" : "-";

  return {
    r1,
    r2,
    k,
    a,
    b,
    c,
    d,
    da,
    db,
    dc,
    askIncreasing,
    askLabel,
    expected: sortIntervals(expected),
    expectedCount: expected.length,
    leftSign,
    middleSign,
    rightSign,
    functionLatex: cubicLatex(a, b, c, d),
    derivativeLatex: derivativeLatex(da, db, dc),
  };
}

function intervalObjToLatex(I) {
  const L = I.left === -Infinity ? "-\\infty" : I.left;
  const R = I.right === Infinity ? "\\infty" : I.right;
  const l = I.leftClosed ? "[" : "(";
  const r = I.rightClosed ? "]" : ")";
  return `${l}${L};\\,${R}${r}`;
}

export function generateTask() {
  const data = buildTaskData();
  return {
    type: "CubicMonotonicityTask",
    prompt: `Podaj przedzialy, na ktorych funkcja f(x) = ${data.functionLatex} jest ${data.askLabel}.`,
    answer: data.expected.map(intervalObjToLatex).join(", "),
    data,
  };
}

export function renderTask({ data }) {
  return (
    <div className="space-y-2">
      <p>Podaj przedzialy, na ktorych funkcja jest <strong>{data.askLabel}</strong>:</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <p>Zaznacz liczbe przedzialow i wpisz je osobno.</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  const value = userAnswer && typeof userAnswer === "object" ? userAnswer : {};
  const count = value.count || "";
  return (
    <div className="space-y-3">
      <div>
        <div style={{ marginBottom: "0.35rem" }}>Liczba przedzialow:</div>
        <select
          value={count}
          onChange={(e) => onChange({ ...value, count: e.target.value })}
          disabled={disabled}
          className="input-secondary"
          style={{ maxWidth: 220 }}
        >
          <option value="">-- wybierz --</option>
          <option value="1">1</option>
          <option value="2">2</option>
        </select>
      </div>

      <div>
        <div style={{ marginBottom: "0.35rem" }}>Przedzial 1:</div>
        <IntervalInput
          value={value.i1 || ""}
          onChange={(next) => onChange({ ...value, i1: next })}
          disabled={disabled}
          asString
          placeholder="np. (-inf; 1)"
        />
      </div>

      {(count === "2" || value.i2) ? (
        <div>
          <div style={{ marginBottom: "0.35rem" }}>Przedzial 2:</div>
          <IntervalInput
            value={value.i2 || ""}
            onChange={(next) => onChange({ ...value, i2: next })}
            disabled={disabled}
            asString
            placeholder="np. (3; +inf)"
          />
        </div>
      ) : null}
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") return false;
  const count = Number(userAnswer.count);
  if (![1, 2].includes(count)) return false;
  if (count !== data.expectedCount) return false;

  const i1 = parseSingleInterval(userAnswer.i1 || "");
  if (!i1) return false;

  const userIntervals = [i1];
  if (count === 2) {
    const i2 = parseSingleInterval(userAnswer.i2 || "");
    if (!i2) return false;
    userIntervals.push(i2);
  }

  const got = sortIntervals(userIntervals);
  const exp = data.expected;
  if (got.length !== exp.length) return false;
  return got.every((u, idx) => intervalEq(u, exp[idx]));
}

export function renderUserAnswer(_taskOrAnswer, maybeUserAnswer) {
  const ua = maybeUserAnswer !== undefined ? maybeUserAnswer : _taskOrAnswer;
  if (!ua || typeof ua !== "object") return <i>(brak odpowiedzi)</i>;
  const count = ua.count || "?";
  const i1raw = ua.i1 || "";
  const i2raw = ua.i2 || "";
  const i1 = i1raw ? intervalToLatex(i1raw, { withX: false }) : "";
  const i2 = i2raw ? intervalToLatex(i2raw, { withX: false }) : "";

  if (!i1 && !i2) return <i>(brak odpowiedzi)</i>;
  return (
    <div className="space-y-1">
      <div>Liczba przedzialow: <strong>{count}</strong></div>
      {i1 ? <div><InlineMath math={`I_1 = ${i1}`} /></div> : null}
      {i2 ? <div><InlineMath math={`I_2 = ${i2}`} /></div> : null}
    </div>
  );
}

export function renderAnswer({ data }) {
  return (
    <div className="space-y-1">
      <div>Liczba przedzialow: <strong>{data.expectedCount}</strong></div>
      {data.expected.map((I, idx) => (
        <div key={`ans-${idx}`}>
          <InlineMath math={`I_${idx + 1} = ${intervalObjToLatex(I)}`} />
        </div>
      ))}
    </div>
  );
}

export function renderExplanation({ data }) {
  const A = data.da;
  const B = data.db;
  const C = data.dc;
  const disc = B * B - 4 * A * C;
  const sqrtDisc = Math.round(Math.sqrt(Math.abs(disc)));
  const inequality = data.askIncreasing ? ">" : "<";
  const between = data.expectedCount === 1;
  const aPos = A > 0;
  const chooseTxt = data.askIncreasing
    ? "szukamy czesci wykresu pochodnej powyzej osi OX"
    : "szukamy czesci wykresu pochodnej ponizej osi OX";
  const x1Lines = String.raw`
\begin{aligned}
x_1 &= \dfrac{-b-\sqrt{\Delta}}{2a}\\[4pt]
x_1 &= \dfrac{${-B}-${sqrtDisc}}{2\cdot ${A}}\\[4pt]
x_1 &= ${data.r1}
\end{aligned}
`;
  const x2Lines = String.raw`
\begin{aligned}
x_2 &= \dfrac{-b+\sqrt{\Delta}}{2a}\\[4pt]
x_2 &= \dfrac{${-B}+${sqrtDisc}}{2\cdot ${A}}\\[4pt]
x_2 &= ${data.r2}
\end{aligned}
`;
  return (
    <div className="space-y-2" style={{ textAlign: "left" }}>
      <p><strong>Krok 1:</strong> Liczymy pochodna funkcji.</p>
      <BlockMath math={`f(x) = ${data.functionLatex}`} />
      <BlockMath math={`f'(x) = ${data.derivativeLatex}`} />

      <p><strong>Krok 2:</strong> Rozwiazujemy nierownosc pochodnej <InlineMath math={`f'(x) ${inequality} 0`} />.</p>
      <BlockMath math={`${data.derivativeLatex} ${inequality} 0`} />
      <BlockMath math={`a=${A},\\ b=${B},\\ c=${C}`} />
      <BlockMath math={`\\Delta = b^2 - 4ac = (${B})^2 - 4\\cdot ${A}\\cdot ${C} = ${disc}`} />
      <BlockMath math={`\\sqrt{\\Delta} = \\sqrt{${disc}} = ${sqrtDisc}`} />
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "1rem",
          alignItems: "start",
        }}
      >
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={"x_1"} />
          </div>
          <BlockMath math={x1Lines} />
        </div>
        <div>
          <div style={{ textAlign: "center", fontWeight: 600 }}>
            <InlineMath math={"x_2"} />
          </div>
          <BlockMath math={x2Lines} />
        </div>
      </div>

      <p><strong>Krok 3:</strong> Rysujemy szkic paraboli pochodnej i odczytujemy przedzialy.</p>
      <p>
        Poniewaz <InlineMath math={`a=${A}`} /> ({aPos ? "dodatnie" : "ujemne"}), parabola ma
        {aPos ? " ramiona do gory" : " ramiona w dol"} i przechodzi przez
        miejsca zerowe <InlineMath math={`x_1=${data.r1},\\ x_2=${data.r2}`} />.
        Teraz {chooseTxt}.
      </p>
      <div style={{ maxWidth: 620, margin: "0.5rem auto" }}>
        <ParabolaShape
          aSign={aPos ? 1 : -1}
          xRange={6}
          yWindow={6}
          showXAxis
          showZeroTicks
          curveColor="#f59e0b"
          labelLeftTex={String(data.r1)}
          labelRightTex={String(data.r2)}
          highlightMode={between ? "between" : "outside"}
          highlightLeftClosed={false}
          highlightRightClosed={false}
          highlightColor="#a78bfa"
        />
      </div>

      <p><strong>Krok 4:</strong> Zapisujemy odpowiedz.</p>
      <div className="space-y-1">
        <div>Liczba przedzialow: <strong>{data.expectedCount}</strong></div>
        {data.expected.map((I, idx) => (
          <div key={`exp-${idx}`}>
            <InlineMath math={`I_${idx + 1} = ${intervalObjToLatex(I)}`} />
          </div>
        ))}
      </div>
      <div style={{ maxWidth: 620, margin: "0.5rem auto" }}>
        {between ? (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              {
                from: data.r1,
                to: data.r2,
                leftClosed: false,
                rightClosed: false,
                leftLabelLatex: String(data.r1),
                rightLabelLatex: String(data.r2),
              },
            ]}
          />
        ) : (
          <NumberLineRanges
            lockPair
            showZero
            ranges={[
              { to: data.r1, rightClosed: false, rightLabelLatex: String(data.r1) },
              { from: data.r2, leftClosed: false, leftLabelLatex: String(data.r2) },
            ]}
          />
        )}
      </div>
    </div>
  );
}
